import { CalendarDays } from 'lucide-react';
import { DateTime } from 'luxon';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getDateFnsLocale } from '@/lib/dateFnsLocale';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface CalendarInputProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  showError?: boolean;
  disabled?: (date: Date) => boolean;
  className?: string;
  fromYear?: number;
  toYear?: number;
  disabledDate?: boolean;
}

export function CalendarInput({
  value,
  onChange,
  label,
  error,
  showError,
  disabled,
  className,
  fromYear,
  toYear,
  disabledDate,
}: CalendarInputProps) {
  const { t, i18n } = useTranslation('common');
  const selectedDate = value ? DateTime.fromISO(value).toJSDate() : undefined;

  const defaultDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 3);
    return dateToCheck < today || dateToCheck > maxDate;
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      onChange(DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'));
    }
  };

  return (
    <div className={className}>
      {disabledDate ? (
        <div
          className={cn(
            'h-14 border bg-white text-tertiary border-input  rounded-lg px-4 py-2 flex items-center justify-between cursor-not-allowed opacity-60',
            showError && 'border-error text-error',
          )}
        >
          {value ? (
            <>
              <div>
                {label && <p className="text-sm text-disable">{label}</p>}
                <p className={cn('text-disable', showError && 'text-error')}>
                  {DateTime.fromISO(value).toFormat('dd/MM/yyyy')}
                </p>
              </div>
              <CalendarDays color={showError ? '#ef4444' : '#9e9fa6'} size={24} />
            </>
          ) : (
            <>
              <p className={cn('text-base text-disable', showError && 'text-error')}>
                {label || t('date')}
              </p>
              <CalendarDays color={'#9E9FA6'} size={24} />
            </>
          )}
        </div>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <div
              className={cn(
                'h-14 border bg-white text-tertiary border-input rounded-lg px-4 py-2 flex items-center justify-between cursor-pointer',
                showError && 'border-error text-error',
              )}
            >
              {value ? (
                <>
                  <div>
                    {label && <p className="text-sm">{label}</p>}
                    <p className={cn('text-black', showError && 'text-error')}>
                      {DateTime.fromISO(value).toFormat('dd/MM/yyyy')}
                    </p>
                  </div>
                  <CalendarDays color={showError ? '#ef4444' : 'black'} size={24} />
                </>
              ) : (
                <>
                  <p className={cn('text-base', showError && 'text-error')}>{label || t('date')}</p>
                  <CalendarDays color={showError ? '#ef4444' : 'black'} size={24} />
                </>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-(--radix-popover-trigger-width) overflow-hidden p-0 border-input"
            align="start"
          >
            <Calendar
              mode="single"
              selected={selectedDate}
              captionLayout="dropdown"
              locale={getDateFnsLocale(i18n.language)}
              className="w-full"
              fromYear={fromYear ?? new Date().getFullYear()}
              toYear={toYear ?? new Date().getFullYear() + 100}
              classNames={{
                day: 'w-full ',
              }}
              disabled={disabled ?? defaultDisabled}
              onSelect={handleDateSelect}
            />
          </PopoverContent>
        </Popover>
      )}
      {showError && error && <p className="mt-1 text-sm text-error">{error}</p>}
    </div>
  );
}
